function isPortalHostVisible(el: HTMLElement): boolean {
  if (!el.isConnected) return false;
  let node: HTMLElement | null = el;
  while (node && node !== document.documentElement) {
    const style = getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden") {
      return false;
    }
    node = node.parentElement;
  }
  return true;
}

/**
 * qiankun `experimentalStyleIsolation` 会用 `div[data-qiankun="应用名"]` 作为 CSS 前缀（见 qiankun
 * `patchers/css.js`）；挂到 `document.body` 的节点不在该祖先下，子应用样式会整段失效。
 *
 * `document.getElementById("root")` 在集成环境下会命中**主应用**的 `#root`，不能把 Portal 挂在那里。
 *
 * 解析顺序：子应用 mount 时写入的 ref → `#subapp-container` 内查找 → 独立运行时的 `#root` → body。
 * 主应用本地页（如 `/community`）会隐藏 `#subapp-container`，此时应回落到 `document.body`，避免弹层挂到不可见节点。
 */
export function getSubAppPortalContainer(): HTMLElement {
  const fromMount = (
    typeof window !== "undefined"
      ? (window as Window & { __EWEADN_QIANKUN_PORTAL_ROOT__?: HTMLElement })
          .__EWEADN_QIANKUN_PORTAL_ROOT__
      : undefined
  ) as HTMLElement | undefined;
  if (
    fromMount instanceof HTMLElement &&
    typeof document !== "undefined" &&
    document.documentElement.contains(fromMount) &&
    isPortalHostVisible(fromMount)
  ) {
    return fromMount;
  }

  const sub = document.getElementById("subapp-container");
  if (sub && isPortalHostVisible(sub)) {
    const byId = sub.querySelector<HTMLElement>("#root");
    if (byId) {
      return byId;
    }
    const scoped = sub.querySelector<HTMLElement>("[data-qiankun]");
    if (scoped) {
      const inner = scoped.querySelector<HTMLElement>("#root");
      return inner ?? scoped;
    }
  }

  if (!sub) {
    const solo = document.getElementById("root");
    if (solo) {
      return solo;
    }
  }

  return document.body;
}
